import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useRouter } from 'next/router'
import { AlertTriangle, Lock, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, ButtonLink } from '@/components/ui/button'

interface EmptyStateProps {
    icon: LucideIcon
    title: string
    description?: ReactNode
    action?: ReactNode
    className?: string
    compact?: boolean
}

export const EmptyState: FC<EmptyStateProps> = ({ icon: Icon, title, description, action, className, compact }) => (
    <div
        className={cn(
            'flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/60 text-center',
            compact ? 'gap-2 px-6 py-8' : 'gap-3 px-6 py-14',
            className
        )}
    >
        <span className='flex size-12 items-center justify-center rounded-2xl bg-primary/8 text-primary'>
            <Icon className='size-6' />
        </span>
        <h3 className='text-base font-semibold text-foreground'>{title}</h3>
        {description && <p className='max-w-md text-sm leading-relaxed text-muted-foreground'>{description}</p>}
        {action && <div className='mt-2 flex flex-wrap items-center justify-center gap-2'>{action}</div>}
    </div>
)

interface ErrorStateProps {
    title?: string
    description?: string
    onRetry?: () => void
    className?: string
}

export const ErrorState: FC<ErrorStateProps> = ({
    title = 'Something went wrong',
    description = 'We could not load this right now. Your data is safe. Please try again in a moment.',
    onRetry,
    className
}) => {
    const router = useRouter()

    return (
        <div role='alert' className={cn('flex flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 px-6 py-12 text-center', className)}>
            <span className='flex size-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive'>
                <AlertTriangle className='size-6' />
            </span>
            <h3 className='text-base font-semibold text-foreground'>{title}</h3>
            <p className='max-w-md text-sm leading-relaxed text-muted-foreground'>{description}</p>
            <Button
                variant='outline'
                className='mt-2'
                onClick={() => (onRetry ? onRetry() : router.replace(router.asPath))}
            >
                <RotateCw />
                Try again
            </Button>
        </div>
    )
}

export const Forbidden: FC = () => (
    <div className='container-page py-20'>
        <EmptyState
            icon={Lock}
            title='You do not have access to this area'
            description='The admin workspace is only open to site administrators. If you should have access, ask the site owner to add your email address.'
            action={(
                <ButtonLink href='/dashboard'>
                    Go to your dashboard
                </ButtonLink>
            )}
        />
    </div>
)

interface PageGateProps {
    forbidden?: boolean
    pageError?: string | null
    children: ReactNode
}

/** Shows the access or error state a page loader reported, instead of the page itself. */
export const PageGate: FC<PageGateProps> = ({ forbidden, pageError, children }) => {
    if (forbidden) return <Forbidden />

    if (pageError) {
        return (
            <div className='container-page py-16'>
                <ErrorState description={`${pageError} Your data is safe. Please try again in a moment.`} />
            </div>
        )
    }

    return <>{children}</>
}
